import {ApolloClient, useApolloClient} from "@apollo/client";
import {Form} from "@/utils/Form";
import {
    CreateExtensionMutation,
    CreateExtensionMutationVariables,
    useCreateExtensionMutation,
} from "./queries.generated";
import {DataSource} from "./DataSource";
import {CreateUserData} from "@/api";
import {Error, UserPackageInfo} from "@/api";
import ErrorHelper from "@/utils/ErrorHelper";
import {Translations, useTranslations} from "@/context/TranslationProvider";
import {showNotification} from "@/actions";
import {Limit, LimitError} from "../types";
import {validateCapabilitySet, validateDeviceTypeSet} from "../tools";
import {Auth, useAuth} from "@/context/AuthProvider";
import {UNLIMITED} from "@/constants";
import {defineMessage} from "react-intl";

export type CreateExtensionFormData = CreateUserData;

export class CreateExtensionForm extends Form<
    CreateExtensionMutation,
    CreateExtensionMutationVariables,
    CreateExtensionFormData,
    CreateExtensionFormData
> {
    readonly dataSource: DataSource;
    readonly auth: Auth;
    readonly isSuperuser: boolean;
    readonly domainId: number;
    protected client: ApolloClient<object>;
    private translations: Translations;
    private validation_errors: LimitError[] = [];

    constructor(
        dataSource: DataSource,
        initialData: CreateExtensionFormData,
        domainId: number,
        successNotification: (message: any, type?: string, context?: any) => void,
    ) {
        super("createUser");
        this.hooks(useCreateExtensionMutation, initialData, successNotification);
        this.dataSource = dataSource;
        this.domainId = domainId;
        this.client = useApolloClient();
        this.translations = useTranslations();
        this.auth = useAuth();
        this.isSuperuser = this.auth.isSuperuser;
    }

    override async submit(): Promise<boolean> {
        const res = await super.submit();

        if (!res) {
            const topLevelErrorHelper = super.createErrorHelper(this.result.data.createUser.errors, 0);
            const genericErrors = topLevelErrorHelper.fieldErrors("data");
            if (genericErrors.length) {
                this.dispatch(
                    // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
                    showNotification(defineMessage({id: genericErrors[0].type}), "error", genericErrors[0].context),
                );
            }
        }

        return res;
    }

    protected override get filteredVariables(): CreateExtensionMutationVariables {
        const values = structuredClone(this.values);

        values.profiles = [
            {name: this.translations.get("extension.default_profile_name_1"), active: true},
            {name: this.translations.get("extension.default_profile_name_2"), active: false},
            {name: this.translations.get("extension.default_profile_name_3"), active: false},
        ];

        if (!values.login.email?.length) delete values.login;
        if (!values.e164) delete values.e164;

        // we can set the value to 0 for device type sets that are unlimited in the user package
        // so then it will not allocate more dts for the user than it should
        const packageUnlimitedDts = this.dataSource.userPackages
            .find((p) => p.name === values.userPackageName)
            ?.deviceTypeSets.filter((d) => d.quantity === UNLIMITED)
            .map((d) => d.name);

        for (const dts of values.deviceTypeSets) {
            if (packageUnlimitedDts?.includes(dts.name)) {
                dts.value = 0;
            }
        }

        return {
            data: values,
        };
    }

    protected override createErrorHelper(errors: Error[]): ErrorHelper {
        return super.createErrorHelper(errors, 1);
    }

    // Get the capabilities mapped to Limit
    get capabilityLimits(): Limit[] {
        return this.dataSource.domainQuotas.capabilitySets.map((cap) => ({
            form: this.values.capabilitySets?.some((c) => c === cap.name) ? 1 : 0,
            up: this.selectedUserPackage?.capabilitySets?.some((c) => c === cap.name) ? 1 : 0,
            name: cap.name,
            user: {
                used: 0,
                allocated: 0,
            },
            system: {
                allocated: this.dataSource.superuserInformation?.totalCapabilityUsage[cap.name] ?? 0,
                limit: cap.systemLimit,
            },
            domain: {
                used: cap.domainUsed,
                allocated: cap.domainAllocated,
            },
        }));
    }

    // Get the devices mapped to Limit
    get deviceTypeSetLimits(): Limit[] {
        return this.dataSource.domainQuotas.deviceTypeSets.map((dev) => ({
            form: this.values.deviceTypeSets?.find((d) => d.name === dev.name)?.value ?? 0,
            up: this.selectedUserPackage?.deviceTypeSets?.find((d) => d.name === dev.name)?.quantity ?? 0,
            name: dev.name,
            user: {
                used: 0,
                allocated: 0,
            },
            system: {
                allocated: this.dataSource.superuserInformation?.totalDeviceUsage[dev.name] ?? 0,
                limit: dev.systemLimit,
            },
            domain: {
                used: dev.domainUsed,
                allocated: dev.domainAllocated,
            },
        }));
    }

    // Get the currently selected user package in form, or null if none
    get selectedUserPackage(): UserPackageInfo | null {
        return this.dataSource.userPackages.find((p) => p.name === this.values.userPackageName) ?? null;
    }

    get validationErrors(): LimitError[] {
        return this.validation_errors;
    }

    setValidationErrors(errors: LimitError[]) {
        this.validation_errors = errors;
    }

    setDeviceTypeSetQuota(name: string, value: number) {
        const values = structuredClone(this.values.deviceTypeSets);
        const set = values.filter((s) => s.name === name)[0];
        if (set) set.value = value;
        else values.push({name, value});
        this.updateValues({deviceTypeSets: values});
    }

    // Validate a single capability, and handle its errors
    validateCap(cap: Limit) {
        const errors = validateCapabilitySet(cap, this.isSuperuser);

        if (errors) {
            this.validation_errors.push(...errors);
            return;
        }

        const errorIndex = this.validationErrors.findIndex((e) => e.name === cap.name);
        if (errorIndex > -1) {
            this.validation_errors.splice(errorIndex, 1);
        }
    }

    // Validate a single device set, and handle its errors
    validateDevice(set: Limit) {
        const errors = validateDeviceTypeSet(set, this.isSuperuser);
        if (errors) {
            this.validation_errors.push(...errors);
            return;
        }

        const errorIndex = this.validationErrors.findIndex((e) => e.name === set.name);
        if (errorIndex > -1) {
            this.validation_errors.splice(errorIndex, 1);
        }
    }

    validateCapabilities() {
        this.capabilityLimits.forEach((cap) => {
            this.validateCap(cap);
        });
    }

    validateDeviceTypeSets() {
        this.deviceTypeSetLimits.forEach((set) => {
            this.validateDevice(set);
        });
    }

    // Run validators
    validate() {
        this.setValidationErrors([]);
        this.validateCapabilities();
        this.validateDeviceTypeSets();
    }

    get valid(): boolean {
        return this.validation_errors.length === 0;
    }
}
